/**
 * Sock-type silhouette images used by the technical passport template and
 * sock-type pickers. The assets live in `apps/engineering/public/images/sock-types`.
 */
export type SockSilhouette =
  | 'no-show'
  | 'ankle'
  | 'quarter'
  | 'mid-crew'
  | 'crew'
  | 'mid-calf'
  | 'over-the-calf'
  | 'knee-high'

export interface SockTypeImageOption {
  value: string
  label: string
  image: string
}

const imageBase = `${import.meta.env.BASE_URL.replace(/\/?$/, '/')}images/sock-types/`

export function sockTypeImage(slug: SockSilhouette): string {
  return `${imageBase}${slug}.png`
}

/**
 * Backend `template_types` codes mapped to the closest sock silhouette.
 * `sport`, `compression` and `custom` have no dedicated drawing, so they reuse
 * the silhouette that best describes the construction.
 */
const TEMPLATE_SILHOUETTES: Record<string, SockSilhouette> = {
  crew: 'crew',
  ankle: 'ankle',
  no_show: 'no-show',
  sport: 'mid-crew',
  compression: 'knee-high',
  custom: 'crew',
}

export function templateTypeImage(code: string): string {
  return sockTypeImage(TEMPLATE_SILHOUETTES[code.trim().toLowerCase()] ?? 'crew')
}

/**
 * Backend `sock_type` values (`uzun` / `orta` / `kalta` / `sledi`).
 * `orta` (mid-crew) is a newer UI option; the backend validator must allow it
 * before a passport using it can be saved.
 */
const SOCK_TYPE_SILHOUETTES: Record<string, SockSilhouette> = {
  uzun: 'crew',
  orta: 'mid-crew',
  kalta: 'ankle',
  sledi: 'no-show',
}

export function passportSockTypeImage(sockType: string): string {
  return sockTypeImage(SOCK_TYPE_SILHOUETTES[sockType.trim().toLowerCase()] ?? 'crew')
}

/**
 * Technical size drawings with pre-printed dimension guides. The measurement
 * values are overlaid on top of these images (`public/images/sock-size0*.jpg`).
 */
const sizeDrawingBase = `${import.meta.env.BASE_URL.replace(/\/?$/, '/')}images/`

const SOCK_SIZE_DRAWINGS: Record<string, string> = {
  uzun: `${sizeDrawingBase}sock-size02.jpg`,
  orta: `${sizeDrawingBase}sock-size02.jpg`,
  kalta: `${sizeDrawingBase}sock-size01.jpg`,
  sledi: `${sizeDrawingBase}sock-size03.jpg`,
}

export function sockSizeDrawing(sockType?: string): string {
  return SOCK_SIZE_DRAWINGS[sockType?.trim().toLowerCase() ?? ''] ?? SOCK_SIZE_DRAWINGS.kalta
}
